import { fieldLimits } from "config";
import LineIcon from "pages/HomePage/components/LineIcon";
import React, { type ChangeEvent, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import styles from "./ExploreSearchBar.module.css";

type ExploreSearchBarProps = {
	isSearching: boolean;
	onSearch: (term: string) => void;
	onValueChange: (value: string) => void;
	value: string;
};

/** Search input + button for the explore page. Submits on Enter or button click. */
const ExploreSearchBar = ({ isSearching, onSearch, onValueChange, value }: ExploreSearchBarProps) => {
	const { t } = useTranslation("explorePage");

	const onInputChange = React.useCallback(
		(event: ChangeEvent<HTMLInputElement>): void => {
			onValueChange(event.target.value);
		},
		[onValueChange]
	);

	const onSubmit = React.useCallback(
		(event: FormEvent<HTMLFormElement>): void => {
			event.preventDefault();
			onSearch(value.trim());
		},
		[onSearch, value]
	);

	return (
		<form className={styles.searchForm} onSubmit={onSubmit} role="search">
			<label className={styles.searchField} htmlFor="explore-search">
				<LineIcon name="search" />
				<span className="sr-only">{t("searchLabel")}</span>
				<input
					id="explore-search"
					maxLength={fieldLimits.search}
					onChange={onInputChange}
					placeholder={t("searchPlaceholder")}
					type="search"
					value={value}
				/>
			</label>

			<button className={styles.searchButton} disabled={isSearching} type="submit">
				<LineIcon name="search" />
				<span>{isSearching ? t("searching") : t("searchButton")}</span>
			</button>
		</form>
	);
};

export default ExploreSearchBar;
